// Work kit — navigator URL-STATE controller (wave 11, §3.3). Mounted on the
// navigator root NEXT TO the engine ("ui--work-navigator-url-state", second
// in the fixed stack). It is the ONLY client-side writer of the navigator's
// five URL params, one writer per param, replaceState-only (never a history
// entry):
//
//   filter       — JSON object {filter_key: [values…]}
//   quickFilters — comma-separated active quick-filter keys
//   sort         — `column:direction` (|absent)
//   columns      — ordered CSV of the VISIBLE column keys
//   selectedIssue— shared with the issue drawer writer (N-23: this
//                  controller only SETS it on ui--work:issue-open; removal
//                  stays the drawer/board owner's — one-writer discipline)
//
// The kit-wide map of every param and its single writer is the registry:
// docs/work/javascript-api.md, "URL-param ownership registry" (W8F).
//
//   - Relay (N-16): the engine's ui--work:filter-toggle / filter-change /
//     filters-clear / sort-change / columns-visibility / issue-open events
//     are relayed into param writes here. No quick-filter state is cached
//     in the engine — the URL + a re-render are the single source of truth.
//   - Fail-closed boot replay: on connect each owned param is VALIDATED;
//     an invalid one is dropped via replaceState and nothing else changes.
//   - Writer discipline (N-20): a param this controller does not own is
//     never read, written, or deleted — board params on a host page
//     survive every navigator write.
//   - Save search (N-21): the [data-work-navigator-save] button is bound
//     HERE (the writer); clicking dispatches ui--work:navigator-save-request
//     {state} carrying the CURRENT five params. No fetch — the host writer
//     persists to its saved_state_url (W10 boundary).
//   - Turbo guard: the boot replay runs once per element (dataset marker);
//     event listeners re-bind on every connect and are removed on
//     disconnect (N-22 — restoration never duplicates listeners).
//
// Invalid input never throws and never writes an error UI: bad values are
// dropped, valid ones land via replaceState.

import { Controller } from "@hotwired/stimulus"

const OWNED_PARAMS = ["filter", "quickFilters", "sort", "columns", "selectedIssue"]
const FILTER_URL_PARAM = "filter"
const QUICK_FILTERS_URL_PARAM = "quickFilters"
const SORT_URL_PARAM = "sort"
const COLUMNS_URL_PARAM = "columns"
const SELECTED_ISSUE_URL_PARAM = "selectedIssue"

const IDENTIFIER = /^[\w.-]+$/
const SORT_SHAPE = /^[\w.-]+:(asc|desc)$/
const REPLAY_MARKER = "data-work-navigator-url-state-booted"

// The engine's event verbs this writer relays into the URL, by param.
const RELAYED_EVENTS = [
  "ui--work:filter-toggle",
  "ui--work:filter-change",
  "ui--work:filters-clear",
  "ui--work:sort-change",
  "ui--work:columns-visibility",
  "ui--work:issue-open",
]

export default class extends Controller {
  connect() {
    // Turbo guard: ONE boot replay per element; the relay listeners must
    // still re-bind on every connect (Turbo morph) and be removed on
    // disconnect — never duplicated (N-22).
    const booted = this.element.hasAttribute(REPLAY_MARKER)
    this.element.setAttribute(REPLAY_MARKER, "true")

    this._handlers = RELAYED_EVENTS.map((type) => {
      const handler = (event) => this._relay(type, event)
      this.element.addEventListener(type, handler)
      return {type, handler}
    })

    this._onSaveClick = (event) => {
      if (!event.target.closest("[data-work-navigator-save]")) return
      this._dispatchSaveRequest()
    }
    this.element.addEventListener("click", this._onSaveClick)

    if (booted) return
    this._bootReplay()
  }

  disconnect() {
    this._handlers?.forEach(({type, handler}) => this.element.removeEventListener(type, handler))
    this._handlers = null
    if (this._onSaveClick) this.element.removeEventListener("click", this._onSaveClick)
  }

  // --- internals ---

  // --- reading (validation-first) ---

  _params() {
    const search = new URLSearchParams(window.location.search)
    return {
      filter: this._readFilter(search.get(FILTER_URL_PARAM)),
      quickFilters: this._readCsv(search.get(QUICK_FILTERS_URL_PARAM)),
      sort: this._readSort(search.get(SORT_URL_PARAM)),
      columns: this._readCsv(search.get(COLUMNS_URL_PARAM)),
      selectedIssue: this._readIdentifier(search.get(SELECTED_ISSUE_URL_PARAM)),
    }
  }

  _readFilter(raw) {
    if (raw == null) return null
    try {
      const parsed = JSON.parse(raw)
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null
      const filter = {}
      for (const [key, values] of Object.entries(parsed)) {
        if (!IDENTIFIER.test(key) || !Array.isArray(values)) return null
        if (values.some((value) => typeof value !== "string" || !IDENTIFIER.test(value))) return null
        if (values.length > 0) filter[key] = values
      }
      return filter
    } catch {
      return null
    }
  }

  _readCsv(raw) {
    if (raw == null) return null
    if (raw === "") return []
    const parts = raw.split(",")
    if (parts.some((part) => !IDENTIFIER.test(part))) return null
    return parts
  }

  _readSort(raw) {
    if (raw == null) return null
    return SORT_SHAPE.test(raw) ? raw : null
  }

  _readIdentifier(raw) {
    if (raw == null) return null
    return IDENTIFIER.test(raw) ? raw : null
  }

  // --- writing (only owned params are ever touched — N-20) ---

  _writeParam(name, rawValue) {
    const url = new URL(window.location.href)
    if (rawValue === null || rawValue === undefined) url.searchParams.delete(name)
    else url.searchParams.set(name, rawValue)
    window.history.replaceState(window.history.state, "", url)
  }

  _writeFilter(filter) {
    const keys = Object.keys(filter)
    this._writeParam(FILTER_URL_PARAM, keys.length === 0 ? null : JSON.stringify(filter))
  }

  // Boot replay (fail-closed): validate the five; an invalid one is dropped
  // and everything else is left byte-identical — valid params are NOT
  // rewritten, they pass through untouched.
  _bootReplay() {
    const search = new URLSearchParams(window.location.search)
    const params = this._params()
    const canonical = {
      [FILTER_URL_PARAM]: params.filter,
      [QUICK_FILTERS_URL_PARAM]: params.quickFilters,
      [SORT_URL_PARAM]: params.sort,
      [COLUMNS_URL_PARAM]: params.columns,
      [SELECTED_ISSUE_URL_PARAM]: params.selectedIssue,
    }
    for (const [name, value] of Object.entries(canonical)) {
      if (!search.has(name)) continue
      if (value === null) search.delete(name)
    }
    // Only rewrite when something actually changed (idempotent connect).
    const before = window.location.search
    const url = new URL(window.location.href)
    url.search = ""
    for (const [key, value] of search.entries()) url.searchParams.set(key, value)
    const after = url.search
    if (before === after) return
    url.search = after
    window.history.replaceState(window.history.state, "", url)
  }

  // --- relay (N-16): engine events → param writes ---

  _relay(type, event) {
    const detail = event.detail || {}
    switch (type) {
      case "ui--work:filter-toggle": {
        const {filter_key, active} = detail
        if (!this._isIdentifier(filter_key)) return
        const current = new URLSearchParams(window.location.search).get(QUICK_FILTERS_URL_PARAM)
        const keys = (current == null ? [] : this._readCsv(current)) || []
        const next = keys.filter((key) => key !== filter_key)
        if (active) next.push(filter_key)
        this._writeParam(QUICK_FILTERS_URL_PARAM, next.length === 0 ? null : next.join(","))
        break
      }
      case "ui--work:filter-change": {
        const {filter_key, values} = detail
        if (!this._isIdentifier(filter_key) || !Array.isArray(values)) return
        if (!values.every((value) => typeof value === "string" && IDENTIFIER.test(value))) return
        const filter = {...(this._params().filter || {})}
        if (values.length === 0) delete filter[filter_key]
        else filter[filter_key] = values
        this._writeFilter(filter)
        break
      }
      case "ui--work:filters-clear": {
        // Clear-all clears BOTH filter params (documented decision — the
        // whole filter surface resets, one empty-payload event).
        this._writeParam(FILTER_URL_PARAM, null)
        this._writeParam(QUICK_FILTERS_URL_PARAM, null)
        break
      }
      case "ui--work:sort-change": {
        const {column, direction} = detail
        if (!this._isIdentifier(column)) return
        this._writeParam(SORT_URL_PARAM, direction ? `${column}:${direction}` : null)
        break
      }
      case "ui--work:columns-visibility": {
        const {visible_keys} = detail
        if (!Array.isArray(visible_keys) || !visible_keys.every((key) => this._isIdentifier(key))) return
        this._writeParam(COLUMNS_URL_PARAM, visible_keys.length === 0 ? null : visible_keys.join(","))
        break
      }
      case "ui--work:issue-open": {
        const {issue_id} = detail
        if (!this._isIdentifier(issue_id)) return
        this._writeParam(SELECTED_ISSUE_URL_PARAM, issue_id)
        break
      }
    }
  }

  // --- save search (N-21): the writer binds the button, never fetches ---

  _dispatchSaveRequest() {
    const params = this._params()
    const state = {
      filter: params.filter || {},
      quickFilters: params.quickFilters || [],
      sort: params.sort || "",
      columns: params.columns || [],
      selectedIssue: params.selectedIssue || "",
    }
    this.element.dispatchEvent(new CustomEvent("ui--work:navigator-save-request", {
      bubbles: true,
      cancelable: true,
      detail: {state},
    }))
  }

  _isIdentifier(value) {
    return typeof value === "string" && IDENTIFIER.test(value)
  }
}
